import { IconPhotoPlus, IconVideoPlus } from "@tabler/icons-react"
import { type FC, useCallback, useEffect, useState } from "react"
import { useDropzone } from "react-dropzone"

import type { LandingPageHeroBlock } from "@forento/core/models/landing"
import { landingPageCopyCharacterLimits } from "@forento/core/schemas/landing"
import { supportedFileExtensions } from "@forento/core/utilities/file"
import { hasLandingPageLinkTarget, type LandingPageLink } from "@forento/core/utilities/landingLink"
import {
	getVimeoIdFromUrl,
	getVimeoUrlFromId,
	getYouTubeIdFromUrl,
	getYouTubeUrlFromId,
} from "@forento/core/utilities/url"
import { VimeoPlayer, YoutubePlayer } from "@forento/ui/components/VideoPlayer"
import { cn } from "@forento/ui/utilities/tailwind"

import Dropdown from "~/components/Dropdown"
import { DeleteIcon } from "~/components/Icon"
import ImageUploader from "~/components/ImageUploader"
import InputField from "~/components/InputField"
import useFileUpload from "~/hooks/useFileUpload"

import ButtonLinkCard from "./ButtonLinkCard"
import EditableField from "./EditableField"
import EditorShell from "./EditorShell"
import RichEditableField from "./RichEditableField"

type Props = {
	block: LandingPageHeroBlock
	onChange: (block: LandingPageHeroBlock) => void
	onClose: () => void
}
const HeroBlockEditor: FC<Props> = ({ block, onChange, onClose }) => {
	const [title, setTitle] = useState(block.content.title)
	const [description, setDescription] = useState(block.content.description)

	const handleDescriptionChange = useCallback((value: string | null) => {
		setDescription(value ?? "")
	}, [])

	const [layout, setLayout] = useState<"split" | "centered">(block.content.layout)
	const [hasButton, setHasButton] = useState(block.content.button !== null)
	const [buttonLabel, setButtonLabel] = useState(block.content.button?.label ?? "")
	const [buttonLink, setButtonLink] = useState<LandingPageLink>(
		block.content.button?.link ?? { type: "page", path: "" },
	)
	const [imageUrl, setImageUrl] = useState(block.content.image?.url ?? "")
	const [imageAlt, setImageAlt] = useState(block.content.image?.alt ?? "")
	const [splitMediaType, setSplitMediaType] = useState<"image" | "youtube" | "vimeo" | "">(
		block.content.media?.type ?? (block.content.image ? "image" : ""),
	)
	const [splitVideoUrl, setSplitVideoUrl] = useState(
		block.content.media?.type === "youtube"
			? getYouTubeUrlFromId(block.content.media.videoId)
			: block.content.media?.type === "vimeo"
				? getVimeoUrlFromId(block.content.media.videoId)
				: "",
	)
	const [backgroundMediaType, setBackgroundMediaType] = useState<"image" | "video" | "">(
		block.content.backgroundMedia?.type ?? "",
	)
	const [backgroundMediaUrl, setBackgroundMediaUrl] = useState(block.content.backgroundMedia?.url ?? "")

	const imageUpload = useFileUpload()
	const videoUpload = useFileUpload()

	useEffect(() => {
		const media =
			splitMediaType === "youtube"
				? getYouTubeIdFromUrl(splitVideoUrl.trim())
				: splitMediaType === "vimeo"
					? getVimeoIdFromUrl(splitVideoUrl.trim())
					: null

		onChange({
			type: "hero",
			content: {
				title: title.trim(),
				description: description.trim(),
				layout,
				button:
					hasButton && buttonLabel.trim() && hasLandingPageLinkTarget(buttonLink)
						? { label: buttonLabel.trim(), link: buttonLink }
						: null,
				image:
					splitMediaType === "image" && imageUrl.trim()
						? { url: imageUrl.trim(), alt: imageAlt.trim() }
						: undefined,
				media:
					splitMediaType === "youtube" && media
						? { type: "youtube", videoId: media }
						: splitMediaType === "vimeo" && media
							? { type: "vimeo", videoId: media }
							: undefined,
				backgroundMedia:
					backgroundMediaType && backgroundMediaUrl.trim()
						? { type: backgroundMediaType as "image" | "video", url: backgroundMediaUrl.trim() }
						: undefined,
			},
		})
	}, [
		backgroundMediaType,
		backgroundMediaUrl,
		buttonLabel,
		buttonLink,
		description,
		hasButton,
		imageAlt,
		imageUrl,
		layout,
		onChange,
		splitMediaType,
		splitVideoUrl,
		title,
	])

	const handleImageUpload = async (file: File, onUploaded: (url: string) => void) => {
		const uploaded = await imageUpload.upload({ file, kind: "landing-image" })
		if (uploaded === null) return
		onUploaded(uploaded.publicUrl)
	}

	const handleVideoUpload = async (file: File) => {
		const uploaded = await videoUpload.upload({ file, kind: "landing-video" })
		if (uploaded === null) return
		setBackgroundMediaUrl(uploaded.publicUrl)
	}

	const videoDropzone = useDropzone({
		accept: { "video/*": supportedFileExtensions.video.map(extension => `.${extension.toLowerCase()}`) },
		multiple: false,
		disabled: videoUpload.isPending,
		async onDrop(acceptedFiles) {
			const file = acceptedFiles[0]
			if (!file) return
			await handleVideoUpload(file)
		},
	})

	const isSplit = layout === "split"
	const splitMedia = (() => {
		if (splitMediaType === "image" && imageUrl) return { type: "image" as const, url: imageUrl, alt: imageAlt }
		if (splitMediaType === "youtube") {
			const videoId = getYouTubeIdFromUrl(splitVideoUrl.trim())
			return videoId ? { type: "youtube" as const, videoId } : null
		}
		if (splitMediaType === "vimeo") {
			const videoId = getVimeoIdFromUrl(splitVideoUrl.trim())
			return videoId ? { type: "vimeo" as const, videoId } : null
		}
		return null
	})()
	const layoutSwitcher = (
		<div className="absolute inset-x-0 top-4 flex justify-center">
			<div className="flex flex-wrap items-center gap-2 rounded-full border border-black/10 bg-white/95 p-1 shadow-sm backdrop-blur">
				<button
					type="button"
					onClick={() => setLayout("centered")}
					className={cn(
						"cursor-pointer rounded-full px-4 py-2 text-sm font-medium transition-colors",
						layout === "centered" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100",
					)}
				>
					Centered
				</button>
				<button
					type="button"
					onClick={() => setLayout("split")}
					className={cn(
						"cursor-pointer rounded-full px-4 py-2 text-sm font-medium transition-colors",
						layout === "split" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100",
					)}
				>
					Split
				</button>
			</div>
		</div>
	)

	return (
		<EditorShell onClose={onClose}>
			<div className="w-full pt-16">
				{isSplit ? (
					<>
						<section className="relative isolate overflow-hidden bg-linear-to-b from-gray-50 via-white to-gray-50 px-6 py-24 sm:py-32 lg:px-8">
							{layoutSwitcher}
							<div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80">
								<div
									className="landing-primary-gradient relative left-[calc(50%-11rem)] aspect-video w-xl -translate-x-1/2 rotate-30 opacity-20 sm:left-[calc(50%-30rem)] sm:w-6xl"
									style={{
										clipPath:
											"polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
									}}
								/>
							</div>
							<div className="relative mx-auto max-w-7xl">
								<div className="grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-12">
									<div className="flex flex-col justify-center">
										<EditableField
											value={title}
											onChange={setTitle}
											placeholder="Hero title"
											multiline
											maxCharacters={landingPageCopyCharacterLimits.title}
											inputClassName="font-bold text-4xl text-gray-900 tracking-tight sm:text-5xl lg:text-6xl"
										/>
										<RichEditableField
											initialValue={description}
											onChange={handleDescriptionChange}
											className="mt-6"
										/>
										{hasButton ? (
											<div className="mt-10 flex flex-wrap items-center gap-4">
												<div className="landing-button inline-flex max-w-full items-center px-6 py-3">
													<EditableField
														value={buttonLabel}
														onChange={setButtonLabel}
														placeholder="Button label"
														fitContent
														maxCharacters={landingPageCopyCharacterLimits.shortLabel}
														showCharacterCounter={false}
														inputClassName="min-w-32 bg-transparent text-center font-semibold text-base"
													/>
												</div>
												<button
													type="button"
													onClick={() => setHasButton(false)}
													className="cursor-pointer text-sm font-medium text-gray-500 transition-colors hover:text-gray-800"
												>
													Remove button
												</button>
											</div>
										) : (
											<button
												type="button"
												onClick={() => {
													setHasButton(true)
													setButtonLabel("Get started")
													setButtonLink({ type: "page", path: "" })
												}}
												className="mt-10 inline-flex w-fit cursor-pointer items-center rounded-full border border-dashed border-gray-300 px-5 py-3 text-sm font-medium text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-800"
											>
												Add button
											</button>
										)}
									</div>
									<div className="relative">
										<div className="relative aspect-video w-full overflow-hidden rounded-lg border border-gray-200/50 bg-gray-50">
											{splitMedia?.type === "image" ? (
												<img
													src={splitMedia.url}
													alt={splitMedia.alt}
													className="size-full object-cover"
												/>
											) : splitMedia?.type === "youtube" ? (
												<YoutubePlayer videoId={splitMedia.videoId} className="h-full" />
											) : splitMedia?.type === "vimeo" ? (
												<VimeoPlayer videoId={splitMedia.videoId} className="h-full" />
											) : (
												<div className="flex size-full items-center justify-center bg-gray-100 text-gray-400">
													<IconPhotoPlus className="size-10" />
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
							<div className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]">
								<div
									className="landing-primary-gradient relative left-[calc(50%+3rem)] aspect-video w-xl -translate-x-1/2 opacity-20 sm:left-[calc(50%+36rem)] sm:w-6xl"
									style={{
										clipPath:
											"polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 34.5%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
									}}
								/>
							</div>
						</section>
						<section className="mt-6 px-6 pb-8 lg:px-8">
							<div className="mx-auto grid max-w-7xl gap-4 lg:grid-cols-3">
								<div className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
									<div className="mb-3 text-sm font-semibold text-gray-900">Hero media</div>
									<Dropdown
										items={[
											{ id: "", title: "None" },
											{ id: "image", title: "Image" },
											{ id: "youtube", title: "YouTube" },
											{ id: "vimeo", title: "Vimeo" },
										]}
										selectedItemId={splitMediaType}
										onChange={id => {
											setSplitMediaType(id as "image" | "youtube" | "vimeo" | "")
											setSplitVideoUrl("")
										}}
									/>
								</div>
								<div className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
									<div className="mb-3 text-sm font-semibold text-gray-900">Media settings</div>
									{splitMediaType === "" && (
										<p className="text-sm text-gray-500">Choose image or embedded video.</p>
									)}
									{splitMediaType === "image" && (
										<div className="flex flex-col gap-4">
											<ImageUploader
												filePath={imageUrl || null}
												onUpload={file => handleImageUpload(file, setImageUrl)}
												onDelete={() => setImageUrl("")}
											/>
											<InputField
												label="Image alt"
												value={imageAlt}
												onChange={setImageAlt}
												placeholder="Alt text"
												inline
											/>
										</div>
									)}
									{(splitMediaType === "youtube" || splitMediaType === "vimeo") && (
										<InputField
											label={`${splitMediaType === "youtube" ? "YouTube" : "Vimeo"} URL`}
											value={splitVideoUrl}
											onChange={setSplitVideoUrl}
											placeholder="https://..."
											inline
										/>
									)}
								</div>
								{hasButton && <ButtonLinkCard value={buttonLink} onChange={setButtonLink} />}
							</div>
						</section>
					</>
				) : (
					<>
						<section
							className={cn(
								"relative isolate overflow-hidden px-6 py-24 sm:py-32 lg:px-8",
								!backgroundMediaType && "bg-linear-to-b from-gray-50 via-white to-gray-50",
							)}
						>
							{layoutSwitcher}
							{backgroundMediaType && backgroundMediaUrl ? (
								<>
									<div className="absolute inset-0 -z-10">
										{backgroundMediaType === "image" ? (
											<img src={backgroundMediaUrl} alt="" className="size-full object-cover" />
										) : (
											<video
												src={backgroundMediaUrl}
												autoPlay
												muted
												loop
												playsInline
												className="size-full object-cover"
											/>
										)}
									</div>
									<div className="absolute inset-0 -z-10 bg-black/40" />
								</>
							) : (
								<>
									<div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80">
										<div
											className="landing-primary-gradient relative left-[calc(50%-11rem)] aspect-video w-xl -translate-x-1/2 rotate-30 opacity-20 sm:left-[calc(50%-30rem)] sm:w-6xl"
											style={{
												clipPath:
													"polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
											}}
										/>
									</div>
									<div className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]">
										<div
											className="landing-primary-gradient relative left-[calc(50%+3rem)] aspect-video w-xl -translate-x-1/2 opacity-20 sm:left-[calc(50%+36rem)] sm:w-6xl"
											style={{
												clipPath:
													"polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 34.5%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
											}}
										/>
									</div>
								</>
							)}
							<div className="relative mx-auto max-w-4xl text-center">
								<EditableField
									value={title}
									onChange={setTitle}
									placeholder="Hero title"
									multiline
									maxCharacters={landingPageCopyCharacterLimits.title}
									inputClassName={cn(
										"text-center text-5xl font-bold tracking-tight sm:text-6xl lg:text-7xl",
										backgroundMediaType && backgroundMediaUrl
											? "text-white placeholder:text-white/60"
											: "text-gray-900",
									)}
								/>
								<RichEditableField
									initialValue={description}
									onChange={handleDescriptionChange}
									className="mt-6"
								/>
								{hasButton ? (
									<div className="mt-10 flex items-center justify-center gap-x-4">
										<div className="landing-button inline-flex max-w-full items-center justify-center px-6 py-3 text-base font-semibold">
											<EditableField
												value={buttonLabel}
												onChange={setButtonLabel}
												placeholder="Button label"
												fitContent
												maxCharacters={landingPageCopyCharacterLimits.shortLabel}
												showCharacterCounter={false}
												inputClassName="min-w-32 bg-transparent text-center font-semibold text-base"
											/>
										</div>
										<button
											type="button"
											onClick={() => setHasButton(false)}
											className={cn(
												"cursor-pointer text-sm font-medium transition-colors",
												backgroundMediaType && backgroundMediaUrl
													? "text-white/80 hover:text-white"
													: "text-gray-500 hover:text-gray-800",
											)}
										>
											Remove button
										</button>
									</div>
								) : (
									<div className="mt-10">
										<button
											type="button"
											onClick={() => {
												setHasButton(true)
												setButtonLabel("Get started")
												setButtonLink({ type: "page", path: "" })
											}}
											className={cn(
												"inline-flex cursor-pointer items-center rounded-full border px-5 py-3 text-sm font-medium transition-colors",
												backgroundMediaType && backgroundMediaUrl
													? "border-white/40 text-white hover:border-white/60"
													: "border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-800",
											)}
										>
											Add button
										</button>
									</div>
								)}
							</div>
						</section>
						<section className="mt-6 px-6 pb-8 lg:px-8">
							<div className="mx-auto grid max-w-7xl gap-4 lg:grid-cols-3">
								{hasButton && <ButtonLinkCard value={buttonLink} onChange={setButtonLink} />}
								<div className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
									<div className="mb-3 text-sm font-semibold text-gray-900">Background media</div>
									<Dropdown
										items={[
											{ id: "", title: "None" },
											{ id: "image", title: "Image" },
											{ id: "video", title: "Video" },
										]}
										selectedItemId={backgroundMediaType}
										onChange={id => {
											setBackgroundMediaType(id as "image" | "video" | "")
											setBackgroundMediaUrl("")
										}}
									/>
								</div>
								<div className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
									<div className="mb-3 text-sm font-semibold text-gray-900">Media upload</div>
									{backgroundMediaType === "" && (
										<p className="text-sm text-gray-500">
											Choose image or video to add a live background.
										</p>
									)}
									{backgroundMediaType === "image" && (
										<ImageUploader
											filePath={backgroundMediaUrl || null}
											onUpload={file => handleImageUpload(file, setBackgroundMediaUrl)}
											onDelete={() => setBackgroundMediaUrl("")}
										/>
									)}
									{backgroundMediaType === "video" &&
										(backgroundMediaUrl ? (
											<div className="relative overflow-hidden rounded-lg border border-stone-200 bg-stone-100">
												<video
													src={backgroundMediaUrl}
													className="max-h-48 w-full object-contain"
													controls
													muted
												/>
												<button
													type="button"
													onClick={() => setBackgroundMediaUrl("")}
													className="absolute top-2 right-2 cursor-pointer rounded-full bg-black/40 p-1.5 transition-colors hover:bg-black/60"
												>
													<DeleteIcon className="size-4 text-white" />
												</button>
											</div>
										) : (
											<div
												{...videoDropzone.getRootProps()}
												className="flex h-32 cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-stone-300 bg-stone-50 p-8 transition-colors hover:border-stone-400 hover:bg-stone-100"
											>
												<input {...videoDropzone.getInputProps()} />
												{videoUpload.isPending ? (
													<p className="text-sm font-medium text-stone-500">
														Uploading video...
													</p>
												) : videoDropzone.isDragActive ? (
													<p className="text-sm font-medium text-stone-600">
														Drop video file here
													</p>
												) : (
													<>
														<IconVideoPlus className="size-8 text-stone-400" />
														<p className="text-center text-sm text-stone-500">
															<span className="font-semibold text-stone-700">
																Click to upload
															</span>{" "}
															or drag a video
														</p>
													</>
												)}
											</div>
										))}
								</div>
							</div>
						</section>
					</>
				)}
			</div>
		</EditorShell>
	)
}

export default HeroBlockEditor
